import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Check, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { setMeta } from "@/lib/seo";

const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "91XXXXXXXXXX";
const waHref = (plan) =>
  `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    `Hi, I want ${plan}. My StayFlow login email is ___ and I want monthly/yearly.`
  )}`;

const PLAN_URL = "https://stayflowapp.in/api/public/pro";

const RESTAURANT_FEATURES = [
  "StayFlow POS, kitchen display and digital menu — bill every plate and every drink, and send orders straight to the kitchen screen.",
  "Car-plate scan and AI dish images for your menu.",
];

const HOMESTAY_FEATURES = [
  "Unlimited bookings you take yourself, billed in your own format, with no booking fee on any of them.",
  "AI Smart Booking — turn a screenshot of a guest's WhatsApp chat into a booking, ready to confirm.",
  "AI ID scan — point the camera at a guest's Aadhaar or passport and their record fills itself.",
];

const PLANS = [
  {
    id: "restaurant",
    name: "StayFlow Restaurant",
    blurb: "Everything you need to run the floor and the kitchen.",
    monthly: "₹499",
    perMonthYearly: "₹417",
    billedYearly: "₹4,999",
    listYearly: "₹5,988",
    savings: "₹989",
    includesLine: null,
    features: RESTAURANT_FEATURES,
  },
  {
    id: "homestay",
    name: "StayFlow Homestay",
    blurb: "For the bookings portals never see — plus the whole restaurant kit.",
    monthly: "₹999",
    perMonthYearly: "₹833",
    billedYearly: "₹9,999",
    listYearly: "₹11,988",
    savings: "₹1,989",
    includesLine: "Includes everything in Restaurant",
    features: HOMESTAY_FEATURES,
  },
];

const STEPS = [
  "Enter the email or phone you registered in StayFlow.",
  "Confirm it is your account and choose monthly or yearly.",
  "Pay. Your plan switches on immediately and your GST invoice appears in the app.",
];

const PlanCard = ({ plan, yearly }) => (
  <div
    className="rounded-3xl border border-black/10 bg-white p-7 md:p-8 flex flex-col"
    data-testid={`plan-card-${plan.id}`}
  >
    <p className="font-heading font-bold text-ink text-base md:text-lg tracking-tight" data-testid={`plan-name-${plan.id}`}>
      {plan.name}
    </p>
    <p className="mt-2 text-sm text-ink-soft leading-relaxed">{plan.blurb}</p>

    <div className="mt-6">
      <p className="font-heading font-extrabold tracking-tighter text-ink text-4xl" data-testid={`plan-price-${plan.id}`}>
        {yearly ? plan.perMonthYearly : plan.monthly}
        <span className="ml-1.5 align-middle text-base font-bold tracking-tight text-ink-soft">/month</span>
      </p>
      {yearly ? (
        <>
          <p className="mt-1.5 text-ink-soft" data-testid={`plan-billed-${plan.id}`}>billed {plan.billedYearly} a year</p>
          <p className="mt-1 text-sm text-ink-faint line-through" data-testid={`plan-strike-${plan.id}`}>{plan.listYearly}</p>
          <p className="mt-1 text-sm font-semibold text-brand-dark" data-testid={`plan-save-${plan.id}`}>You save {plan.savings}</p>
        </>
      ) : (
        <p className="mt-1.5 text-ink-soft" data-testid={`plan-billed-${plan.id}`}>billed monthly</p>
      )}
    </div>

    {plan.includesLine && (
      <p className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink" data-testid={`plan-includes-${plan.id}`}>
        <span className="shrink-0 w-5 h-5 rounded-full bg-brand-soft text-brand-dark flex items-center justify-center">
          <Check className="w-3 h-3" aria-hidden="true" />
        </span>
        {plan.includesLine}
      </p>
    )}

    <ul className={`${plan.includesLine ? "mt-4" : "mt-6"} space-y-4`} data-testid={`plan-features-${plan.id}`}>
      {plan.features.map((f) => (
        <li key={f} className="flex gap-3 text-ink">
          <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-brand-soft text-brand-dark flex items-center justify-center">
            <Check className="w-3.5 h-3.5" aria-hidden="true" />
          </span>
          <span className="text-sm leading-relaxed">{f}</span>
        </li>
      ))}
    </ul>

    <a
      href={PLAN_URL}
      data-testid={`plan-get-${plan.id}`}
      className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white text-sm font-medium min-h-12 py-3 px-4 text-center transition-transform duration-200 hover:-translate-y-0.5"
    >
      Get {plan.name}
      <ArrowRight className="w-4 h-4" aria-hidden="true" />
    </a>
    <a
      href={waHref(plan.name)}
      target="_blank"
      rel="noreferrer"
      data-testid={`plan-whatsapp-${plan.id}`}
      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors"
    >
      <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
      or message us on WhatsApp
    </a>
  </div>
);

const Pro = () => {
  const [yearly, setYearly] = useState(true);

  useEffect(() => {
    setMeta({
      title: "StayFlow plans — Restaurant ₹499/month, Homestay ₹999/month",
      description:
        "StayFlow is free forever for verified hosts. StayFlow Restaurant is ₹499 a month or ₹4,999 a year. StayFlow Homestay includes everything in Restaurant and is ₹999 a month or ₹9,999 a year. All taxes included.",
    });
  }, []);

  return (
    <div className="bg-paper min-h-screen" data-testid="pro-page">
      <Navbar />

      <main className="pt-[76px]">
        {/* Hero */}
        <section className="relative overflow-hidden bg-ink text-white">
          <div aria-hidden="true" className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full bg-brand/25 blur-[120px]" />
          <div className="relative max-w-5xl mx-auto px-6 md:px-12 pt-20 pb-20 md:pt-28 md:pb-28">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <p className="overline text-brand">For hosts</p>
              <h1 className="mt-5 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
                StayFlow plans
              </h1>
              <p className="mt-4 text-lg md:text-xl text-white/70 max-w-2xl">
                Keep more of every booking. Run your place like a pro.
              </p>
              <p className="mt-8 max-w-3xl text-white/60 leading-relaxed">
                StayFlow is free for every verified host, forever. The paid plans are for hosts who want every
                booking, every bill and every guest in one place.
              </p>
              <a
                href={PLAN_URL}
                data-testid="pro-get-btn-hero"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white text-sm font-medium min-h-12 py-3 px-6 text-center transition-transform duration-200 hover:-translate-y-0.5"
              >
                Get a StayFlow plan
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Free, forever */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28">
          <h2 className="font-heading font-bold text-ink text-base md:text-lg tracking-tight">Free, forever</h2>
          <p className="mt-6 max-w-3xl text-ink leading-relaxed" data-testid="pro-free-p1">
            Online booking links, calendar, guest records with ID, coupons and date blocks, your listing on the
            StayFlow marketplace, GST invoices, payouts straight to your bank, reports and staff logins. No monthly
            charge, ever.
          </p>
          <p className="mt-5 max-w-3xl text-ink leading-relaxed" data-testid="pro-free-p2">
            You pay only when a guest books and pays through StayFlow: 5% + GST on that booking. No booking, no
            fee. Travel portals typically keep 15–25% of every stay; StayFlow leaves the rest with you and handles
            the GST on those stays, so you can sell online without your own GST number.
          </p>
        </section>

        {/* Plans + prices */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="pricing-section">
          <h2 className="font-heading font-bold text-ink text-base md:text-lg tracking-tight">
            Choose your plan
          </h2>
          <p className="mt-5 max-w-3xl text-ink leading-relaxed" data-testid="pro-adds-intro">
            Most homestay bookings come by phone, WhatsApp and walk-in. The free plan records 3 of them a
            month. StayFlow Homestay records them all.
          </p>

          {/* Monthly / Yearly toggle */}
          <div className="mt-8 inline-flex rounded-full border border-black/10 bg-white p-1" data-testid="billing-toggle">
            <button
              type="button"
              onClick={() => setYearly(false)}
              aria-pressed={!yearly}
              data-testid="billing-monthly"
              className={`rounded-full px-5 h-10 text-sm font-semibold transition-colors ${!yearly ? "bg-ink text-white" : "text-ink-soft hover:text-ink"}`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setYearly(true)}
              aria-pressed={yearly}
              data-testid="billing-yearly"
              className={`rounded-full px-5 h-10 text-sm font-semibold transition-colors ${yearly ? "bg-ink text-white" : "text-ink-soft hover:text-ink"}`}
            >
              Yearly · Save 17%
            </button>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-6 md:gap-8">
            {PLANS.map((p) => (
              <PlanCard key={p.id} plan={p} yearly={yearly} />
            ))}
          </div>

          <p className="mt-6 text-sm text-ink-soft" data-testid="pricing-terms-line">
            All taxes included · No charges per bill or booking · No auto-debit — renew when you choose
          </p>
          <p className="mt-3 max-w-3xl text-sm text-ink-soft" data-testid="pro-price-note">
            Your GST invoice appears in your app as soon as you pay. Prepaid, no lock-in; unused days are not
            refunded. If you ever stop, you go back to the free plan and nothing is deleted.
          </p>
        </section>

        {/* How to get it */}
        <section className="bg-paper-muted/60 border-y border-black/5">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24">
            <h2 className="font-heading font-bold text-ink text-base md:text-lg tracking-tight">How to get it:</h2>
            <ol className="mt-9 grid md:grid-cols-3 gap-8 md:gap-10" data-testid="pro-steps">
              {STEPS.map((s, i) => (
                <li key={i} className="relative">
                  <span className="font-mono text-sm text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-3 text-ink leading-relaxed">{s}</p>
                </li>
              ))}
            </ol>
            <div className="mt-12 flex flex-col items-start gap-3">
              <a
                href={PLAN_URL}
                data-testid="pro-get-btn-2"
                className="group inline-flex items-center gap-2 rounded-full bg-ink text-white font-medium h-12 px-6 transition-transform duration-200 hover:-translate-y-0.5"
              >
                Get a StayFlow plan
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </a>
              <a
                href={waHref("a StayFlow plan")}
                target="_blank"
                rel="noreferrer"
                data-testid="pro-whatsapp-link-2"
                className="inline-flex items-center gap-1.5 pl-1 text-xs text-ink-soft hover:text-ink transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                or message us on WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Terms */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="overline text-ink-faint">Terms</p>
          <p className="mt-4 max-w-3xl text-ink-soft leading-relaxed" data-testid="pro-terms">
            Prepaid for the period chosen, no auto-debit, no refund for unused days; when it ends you go back to
            the free plan and nothing is deleted.
          </p>
          <Link to="/stays" data-testid="pro-back-stays" className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink transition-colors">
            Browse stays on StayFlow
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Pro;
